import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sticky from 'react-stickynode';
import debounce from 'lodash/debounce';
import { STICKY_STATUS } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { ScrollArrows } from 'site-modules/comparator/components/scroll-arrows/scroll-arrows';

import './comparator-grid-sticky-scroll.scss';

export function comparatorGridStickyScroll(WrappedComponent, { withArrows } = {}) {
  function ComparatorGridStickScroll(props) {
    const comparatorGridRef = useRef();
    const comparatorHeaderRef = useRef();
    const comparatorHeaderStickyRef = useRef();
    const [arrows, setArrows] = useState({ left: false, right: false });

    const checkArrows = useCallback(
      debounce(() => {
        if (!withArrows) {
          return;
        }

        const scrollContainer = comparatorGridRef.current;
        setArrows({
          left: !!scrollContainer.scrollLeft,
          right: scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollContainer.scrollLeft > 1,
        });
      }, 25),
      [withArrows]
    );

    const onArrowClick = useCallback(isRightArrow => {
      comparatorGridRef.current.scrollLeft = isRightArrow ? comparatorGridRef.current.scrollWidth : 0;
    }, []);

    const syncCarHeaderScroll = useCallback(() => {
      comparatorHeaderRef.current.scrollLeft = comparatorGridRef.current.scrollLeft;
      if (comparatorHeaderStickyRef.current) {
        comparatorHeaderStickyRef.current.scrollLeft = comparatorGridRef.current.scrollLeft;
      }
      checkArrows();
    }, [checkArrows]);

    const onStickyStateChange = useCallback(
      ({ status }) => {
        if (status === Sticky.STATUS_FIXED || status === STICKY_STATUS.STICKY) {
          syncCarHeaderScroll();
        }
      },
      [syncCarHeaderScroll]
    );

    useEffect(() => {
      checkArrows();
    }, [checkArrows]);

    return (
      <div className="container px-0 pos-r">
        <div className="comparator-grid-sticky-scroll px-0_5" onScroll={syncCarHeaderScroll} ref={comparatorGridRef}>
          <WrappedComponent
            {...props}
            arrows={arrows}
            onArrowClick={onArrowClick}
            onStickyStateChange={onStickyStateChange}
            comparatorHeaderRef={comparatorHeaderRef}
            comparatorHeaderStickyRef={comparatorHeaderStickyRef}
          />
        </div>
        {!!withArrows && (
          <ClientSideRender>
            <ScrollArrows heightRef={comparatorHeaderRef} onArrowClick={onArrowClick} arrows={arrows} />
          </ClientSideRender>
        )}
      </div>
    );
  }

  ComparatorGridStickScroll.displayName = `ComparatorGridStickScroll(${WrappedComponent.name})`;
  return ComparatorGridStickScroll;
}
